import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * ProviderReconciliation
 *
 * Offline-Excel review flow. Three actions, in order:
 *   1. Generate proposals — runs the clustering pass. Auto-approves
 *      clusters with 100% confidence (no ambiguity possible) and creates
 *      pending proposals for the rest.
 *   2. Download review workbook — .xlsx with one row per cluster,
 *      variants joined in a single cell, Decision column with a dropdown.
 *   3. Upload completed workbook — applies every marked Decision in bulk.
 *
 * The whole review happens in Excel. This screen only exists to trigger
 * generate / download / upload.
 */
function ProviderReconciliation() {
  const [busy, setBusy] = useState(null); // 'generate' | 'download' | 'upload'
  const [generateResult, setGenerateResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const generate = async () => {
    setBusy('generate');
    setError(null);
    setGenerateResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/maylogic/reconciliation/rebuild`,
        {},
        { withCredentials: true }
      );
      setGenerateResult(res.data);
    } catch (e) {
      setError('Generate failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const download = async () => {
    setBusy('download');
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/maylogic/reconciliation/export`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'maylogic-reconciliation-review.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Download failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy('upload');
    setError(null);
    setApplyResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/maylogic/reconciliation/apply`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setApplyResult(res.data);
    } catch (e) {
      setError('Apply failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="lm-eyebrow">MayLogic data quality</div>
      <h1>Provider reconciliation</h1>
      <p className="lm-subtitle">
        Bring provider name variants together so every history tells the full
        story — "Dr. Troy Mounts" and "Dr. Mounts" are one provider, and
        MayLogic should aggregate them as one. The review itself happens in
        Excel, not on this page.
      </p>

      {error && <div className="lm-error">{error}</div>}

      <section className="lm-panel">
        <div className="lm-step">
          <span className="lm-stepnum">1</span>
          <div>
            <h3>Generate proposals</h3>
            <p>
              Identifies likely matches across provider names. Matches at 100%
              confidence are approved automatically; ambiguous ones are flagged
              for your review.
            </p>
            <button type="button" className="lm-button lm-primary"
                    onClick={generate} disabled={busy === 'generate'}>
              {busy === 'generate' ? 'Generating…' : 'Generate proposals'}
            </button>
            {generateResult && (
              <div className="lm-toast">
                Scanned {generateResult.raw_names_scanned?.toLocaleString()} raw provider names.{' '}
                <strong>{(generateResult.auto_approved_count || 0).toLocaleString()}</strong> obvious merges applied
                automatically ({(generateResult.auto_approved_rows_backfilled || 0).toLocaleString()} historical rows now aggregated).{' '}
                <strong>{(generateResult.proposals_written || 0).toLocaleString()}</strong> flagged for review.
                {(generateResult.long_names_excluded || generateResult.oversized_clusters_skipped || generateResult.long_canonical_clusters_skipped) ? (
                  <div style={{ marginTop: 8, fontSize: 11, opacity: 0.85 }}>
                    Safeguards:{' '}
                    {generateResult.long_names_excluded ? `${generateResult.long_names_excluded.toLocaleString()} long compound names excluded from clustering · ` : ''}
                    {generateResult.oversized_clusters_skipped ? `${generateResult.oversized_clusters_skipped} oversized clusters (>20 members) skipped · ` : ''}
                    {generateResult.long_canonical_clusters_skipped ? `${generateResult.long_canonical_clusters_skipped} long-canonical clusters skipped` : ''}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="lm-panel" style={{ marginTop: 14 }}>
        <div className="lm-step">
          <span className="lm-stepnum">2</span>
          <div>
            <h3>Review the workbook</h3>
            <p>
              One row per cluster. Open it in Excel or Google Sheets, sort by
              confidence, and scan the auto-approved section for anything that
              looks wrong. Mark the <em>Decision</em> column: keep, undo,
              approve, reject, or remap.
            </p>
            <button type="button" className="lm-button"
                    onClick={download} disabled={busy === 'download'}>
              {busy === 'download' ? 'Preparing…' : 'Download workbook (.xlsx)'}
            </button>
          </div>
        </div>
      </section>

      <section className="lm-panel" style={{ marginTop: 14 }}>
        <div className="lm-step">
          <span className="lm-stepnum">3</span>
          <div>
            <h3>Apply the completed workbook</h3>
            <p>
              Applies every marked Decision in bulk — undoes the auto-approved
              clusters you flagged, approves the ones you approved, and so on.
            </p>
            {/* The input stays in the DOM and keeps its ref, so the reset
                after an upload still works; the button is only its handle. */}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm"
                   onChange={upload} disabled={busy === 'upload'}
                   className="lm-sr" id="lm-recon-upload" />
            <label htmlFor="lm-recon-upload" className="lm-button lm-filebutton">
              {busy === 'upload' ? 'Applying…' : 'Choose completed workbook…'}
            </label>
            {applyResult && (
              <div className="lm-toast">
                Applied — kept: <strong>{applyResult.counts.kept}</strong> ·
                undone: <strong>{applyResult.counts.undone}</strong> ·
                approved: <strong>{applyResult.counts.approved}</strong> ·
                rejected: <strong>{applyResult.counts.rejected}</strong> ·
                remapped: <strong>{applyResult.counts.remapped}</strong>.{' '}
                {(applyResult.provider_negotiations_updated || 0).toLocaleString()} historical rows updated.
                {applyResult.error_count > 0 && (
                  <div className="lm-error" style={{ marginTop: 10, marginBottom: 0 }}>
                    <strong>{applyResult.error_count} error(s):</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                      {applyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default ProviderReconciliation;
